import { useMemo } from 'react';
import { projectPointPreservingAspect } from '@peruselab/structure-editor/domain/aspectProjection';
import type { PaperSize } from '@peruselab/structure-editor/domain/geometry';
import { renderableAnnotations } from '@peruselab/structure-editor/domain/annotationRendering';
import { aromaticRingCenters, documentToAtoms, documentToLines } from '@peruselab/structure-editor/domain/graphDrawing';
import {
  fusedRingDoubleBondSides,
  nonFusedRingDoubleBondCenters
} from '@peruselab/structure-editor/domain/objectEditing';
import {
  aromaticBondIds as selectAromaticBondIds,
  bondStereoValues,
  drawnBondOrders
} from '@peruselab/structure-editor/domain/documentSelectors';
import { withAtomLabelOverrides } from '@peruselab/structure-editor/domain/drawingDocumentProjections';
import { contractedDisplay } from '@peruselab/structure-editor/domain/contractedGroups';
import type { DrawingDocument } from '../generated/drawing-contract';

/**
 * Coordinate projections and chemistry lookups for useDrawingDocument.ts (roadmap item 35) --
 * every value here is a pure derivation of `document` (+ `paperSize`/`isOversizedImport` for the
 * ones that reproject into surface space), with no state or effects of its own.
 */
export function useDrawingProjections(document: DrawingDocument, paperSize: PaperSize, isOversizedImport: boolean) {
  const coordinateSize = useMemo(
    () => ({
      width: document.depiction.coordinateSet.width,
      height: document.depiction.coordinateSet.height
    }),
    [document.depiction.coordinateSet]
  );
  // #736 step 2 -- a contracted group is drawn (and picked) as one label at its attachment atom.
  const contracted = useMemo(() => contractedDisplay(document), [document]);
  const lines = useMemo(
    () =>
      documentToLines(document)
        .filter((line) => !contracted.hiddenEdgeIds.has(line.id))
        .map((line) => ({
          ...line,
          start: projectPointPreservingAspect(line.start, coordinateSize, paperSize, !isOversizedImport),
          end: projectPointPreservingAspect(line.end, coordinateSize, paperSize, !isOversizedImport)
        })),
    [contracted, coordinateSize, document, isOversizedImport, paperSize]
  );
  const atoms = useMemo(
    () =>
      withAtomLabelOverrides(
        documentToAtoms(document).filter((atom) => !contracted.hiddenAtomIds.has(atom.id)),
        contracted.labels
      ).map((atom) => ({
        ...atom,
        position: projectPointPreservingAspect(atom.position, coordinateSize, paperSize, !isOversizedImport)
      })),
    [contracted, coordinateSize, document, isOversizedImport, paperSize]
  );
  const bondOrders = useMemo(() => drawnBondOrders(document), [document.structure.bonds]);
  const aromaticBondIds = useMemo(
    () => new Set(Object.keys(Object.fromEntries([...selectAromaticBondIds(document)].map((id) => [id, true])))),
    [Object.fromEntries([...selectAromaticBondIds(document)].map((id) => [id, true]))]
  );
  const overriddenBondIds = useMemo(
    () => new Set(Object.keys(document.depiction.bondLengthOverrides)),
    [document.depiction.bondLengthOverrides]
  );
  const stereoBonds = bondStereoValues(document);
  const aromaticCenters = useMemo(() => {
    const centers = Object.fromEntries(
      Object.entries(aromaticRingCenters(document)).map(([edgeId, point]) => [
        edgeId,
        projectPointPreservingAspect(point, coordinateSize, paperSize, !isOversizedImport)
      ])
    ) as Record<string, { x: number; y: number }>;
    // #422: a plain (non-aromatic) double bond in exactly one ring also insets its second line
    // toward that ring's interior. Skip anything the aromatic pass above already placed -- a bond
    // in a fused aromatic system should keep that more specific ring path.
    for (const [edgeId, point] of Object.entries(nonFusedRingDoubleBondCenters(atoms, lines, bondOrders)))
      if (!(edgeId in centers)) centers[edgeId] = point;
    // #453: a fusion double bond (shared by exactly two rings) faces whichever ring the user has
    // explicitly chosen (stored as an atom private to that ring). Left unset, it stays out of this
    // map entirely -- the segment-selection code below then has no ringCenter to work with and
    // falls back to the plain symmetric double-bond rendering, matching neither ring specifically
    // (a deliberately neutral default, since either ring is an equally valid but arbitrary choice).
    for (const [edgeId, sides] of Object.entries(fusedRingDoubleBondSides(atoms, lines, bondOrders))) {
      if (edgeId in centers) continue;
      const chosenAtomId =
        stereoBonds[edgeId]?.type === 'double-bond-side' ? stereoBonds[edgeId].facingAtomId : undefined;
      const side = sides.find((candidate) => candidate.facingAtomId === chosenAtomId);
      if (side) centers[edgeId] = side.center;
    }
    return centers;
  }, [coordinateSize, document, isOversizedImport, paperSize, atoms, lines, bondOrders, stereoBonds]);
  const annotationRenderables = useMemo(
    () => renderableAnnotations(document.annotations, atoms, lines, coordinateSize, paperSize, !isOversizedImport),
    [document.annotations, atoms, lines, coordinateSize, paperSize, isOversizedImport]
  );

  const metrics = useMemo(
    () => ({
      nodes: document.structure.graph.nodes.length,
      edges: document.structure.graph.edges.length,
      atoms: Object.keys(document.structure.atoms).length,
      bonds: Object.keys(document.structure.bonds).length
    }),
    [document]
  );

  return {
    coordinateSize,
    lines,
    atoms,
    bondOrders,
    aromaticBondIds,
    overriddenBondIds,
    aromaticCenters,
    stereoBonds,
    annotationRenderables,
    metrics
  };
}
